import { reportJsonParseException } from '../common/exception';
import { ThemeStyle } from '../components/OrderRequest';

const Intl = Mach.requireModule('Intl');

/**
* 动画模板
* @param keyFrames
* @param duration
* @returns
*/
export function animationTemp(keyFrames: any, duration: number = 300) {
  return {
    duration: `${duration}ms`,
    iterationCount: 1,
    direction: 'normal',
    timingFunction: 'ease-in-out',
    fillMode: 'forward',
    transformOrigin: 'left top',
    delay: '0s',
    keyFrames
  };
}

// 防抖
export function debounce(fn, timeout = 300) {
  let timer;
  return function (this, ...args) {
    if (timer) {
      clearTimeout(timer);
      timer = null;
    }
    timer = setTimeout(() => {
      fn.apply(this, args);
    }, timeout);
  };
}

// 节流
export function throttle(fn, wait = 300) {
  let lastTime = 0;
  return function (this, ...args) {
    const now = +new Date();
    if (now - lastTime > wait) {
      lastTime = now;
      fn.apply(this, args);
    }
  };
}

// 转JSON
export function safeParseJSONStr(str) {
  if (!str || typeof str !== 'string') {
    return null;
  }
  try {
    return JSON.parse(str);
  } catch (error) {
  // 捕获到异常上报
    console.log('safeParseJSONStr', error);
    reportJsonParseException({ content: str, error });
    return null;
  }
}

// 判断是否是对象
export function isPlainObject(obj) {
  return typeof obj === 'object' && obj !== null && !Array.isArray(obj);
}

/**
 * 拆分带有超链接<a></a>的文案
 * @param text 入参
 * @returns
 */
export function hyperlinkItemListWithText(text: string) {
  if (!text) return [];
  // @ts-ignore
  return text.split('<a>').flatMap((part) => {
    const [linkText, remainingText] = part.split('</a>');
    return [
      ...(linkText ? [{ text: linkText, link: !!remainingText }] : []),
      ...(remainingText ? [{ text: remainingText, link: false }] : [])
    ];
  });
}

/**
 * 判断是否是沙特门店
 */
export function isSaudiShopStore(region: string | undefined) {
  return region?.toUpperCase() === 'SA';
}

/**
 * 比较版本
 * v1 > v2，返回1
 * v1 < v2，返回-1
 * v1 = v2，返回0
 * @param v1
 * @param v2
 * @returns
 */
export function compareVersion(v1, v2) {
  if (!v1 || !v2) {
    return -1;
  }

  if (v1 === v2) {
    return 0;
  }
  v1 = `${v1}`.split('.');
  v2 = `${v2}`.split('.');
  const len = Math.max(v1.length, v2.length);

  while (v1.length < len) {
    v1.push('0');
  }
  while (v2.length < len) {
    v2.push('0');
  }

  for (let i = 0; i < len; i++) {
    const num1 = parseInt(v1[i], 10);
    const num2 = parseInt(v2[i], 10);

    if (num1 > num2) {
      return 1;
    } else if (num1 < num2) {
      return -1;
    }
  }
  return 0;
}

/**
 * 是否支持转场动画
 */
export function isSupportTransition(bundleName: string) {
  if (!bundleName) return false;
  const whiteList = [
    'mach_pro_sailor_c_home_page_main', // 首页
    'mach_pro_sailor_c_pick_up_page_main', // 自提落地页
    'mach_pro_sailor_c_quality_special_page_main', //品质专区落地页
    'mach_pro_sailor_c_homepage_shopLandingPage', // 店铺落地页
    'mach_pro_sailor_c_channel_list_main', // 通用落地页
  ];
  const isVersionSupport = compareVersion(Mach.env.version, '1.13.200') !== -1;
  return Mach.env.isIOS && isVersionSupport && whiteList.includes(bundleName);
}

export function createThemeStyleProps(condition: boolean | string, style: ThemeStyle) {
  const styleProps = {
    style: {}
  };

  if (condition) {
    styleProps.style = style;
  }

  return styleProps;
}

export function isObject(input) {
  return Object.prototype.toString.call(input) === '[object Object]';
}

export const reduceStrArrToMap = (strArr) => strArr.reduce((acc, item) => {
  acc[item] = item;

  return acc;
}, {});

/**
 * H5页面拼接泳道名称
 * @param url
 * @returns
 */
export const webURLAppendSwimlane = (url: string) => {
  // url为空或非字符串时，返回原始url
  if (!url || typeof url !== 'string') {
    return url;
  }
  // 获取泳道信息
  const SLCommon = Mach.requireModule('SLCommon');
  const { swimlane } = SLCommon?.getEnv?.() || {};
  const swimlaneName = swimlane?.name || '';
  // 泳道名为空或非字符串时，返回原始域名
  if (!swimlaneName || typeof swimlaneName !== 'string') {
    return url;
  }

  // 使用 split 分割 URL
  const parts = url.split('://');
  if (parts.length !== 2) {
    return url; // 返回原始域名
  }
  const [scheme, path] = parts;
  // 判断连接符 & 或 ?
  const catSt = path.includes('?') ? '&' : '?';
  // 在域名中拼接 swimlane 参数
  return `${scheme}://${swimlaneName}-sl-${path}${catSt}swimlane=${swimlaneName}`;
};

/**
 * 判断是否支持Mach组件
 * @param componentName 组件名称
 * @returns
 */
export const supportMachComponent = (componentName: string) => {
  // @ts-ignore
  const isSupport = Mach.supportComponent?.(componentName);
  return isSupport;
};

/**
 * 功能是否等同SA
 * @param region
 * @returns
 */
export function isLogicFollowSA(region: string | undefined | null): boolean {
  if (!region) {
    return false;
  }
  const regionList = [
    'SA',
    'MG',
    'BR',
    ...(Intl?.getRegionsByArea?.('GCC') || [])
  ];
  return regionList?.includes?.(region?.toUpperCase?.()) || false;
}

/**
 * 是否是SA区域
 * @param region 当前传入的region
 * @returns boolean
 */
export function isEqualSARegion(region: string | undefined | null): boolean {
  if (!region) {
    return false;
  }
  return ['SA', 'MG'].includes(region);
}

/**
 * 是否支持系统相册
 * @returns
 */
export const supportSystemAlbum = () => Mach.env.isAndroid && compareVersion(Mach.env.version, '2.8.500') !== -1;
